import { API_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Tabs.css';

interface SubTab {
  id: string;
  label: string;
  emoji: string;
  path: string;
}

interface Tab {
  id: string;
  label: string;
  emoji: string;
  children?: SubTab[];
  path?: string;
  externalUrl?: string;
}

interface CreditInfo {
  totalCredits: number;
  usedCredits: number;
  remainingCredits: number;
  nextInvoiceDate: string;
  usagePercentage: number;
  monthlyCredits: number;
  billingPeriod?: string;
  remainingDays?: number;
  avgCreditsPerDay?: number;
}

interface GmailAccount {
  email: string;
  alias: string;
  status: 'active' | 'expired' | 'revoked';
  lastSyncedAt: string | null;
}

const tabs: Tab[] = [
  {
    id: 'influencer-marketing',
    label: 'Influencer Marketing',
    emoji: '📢',
    children: [
      { id: 'find', label: 'Find', emoji: '🔍', path: '/find/keywords' },
      { id: 'creators', label: 'Creators', emoji: '🎨', path: '/creators/instagram' },
      { id: 'outreach', label: 'Outreach', emoji: '📋', path: '/outreach/responses' },
      { id: 'collaborations', label: 'Collaborations', emoji: '🤝', path: '/collaborations' },
      { id: 'batch', label: 'Batch', emoji: '📦', path: '/batch' },
    ]
  },
  {
    id: 'email-marketing',
    label: 'Email Marketing',
    emoji: '✉️',
    path: '/email'
  },
  {
    id: 'paid-ads',
    label: 'Paid Ads',
    emoji: '📣',
    path: '/paid-ads'
  },
  {
    id: 'reddit-marketing',
    label: 'Reddit Marketing',
    emoji: '🤖',
    externalUrl: 'https://www.replyagent.ai/dashboard/products/cmlakbomb0001l704c89vy17q/comments'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    emoji: '📊',
    path: '/stats'
  }
];

const Tabs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, savedAccounts, switchAccount, removeAccount } = useAuth();
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [gmailAccounts, setGmailAccounts] = useState<GmailAccount[]>([]);
  const [gmailLoading, setGmailLoading] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/company');
  };
  
  useEffect(() => {
    fetchCreditInfo();
    // Refresh credit info every 10 minutes (increased from 5 minutes)
    const interval = setInterval(fetchCreditInfo, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Gmail accounts when user changes
  useEffect(() => {
    if (user?.email) {
      fetchGmailAccounts();
    } else {
      setGmailAccounts([]);
    }
  }, [user?.email]);

  const fetchGmailAccounts = async () => {
    if (!user?.email) return;
    try {
      const response = await fetch(`${API_URL}/api/gmail/accounts?userId=${encodeURIComponent(user.email)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setGmailAccounts(result.accounts);
        }
      }
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
    }
  };

  const handleGmailConnect = async () => {
    if (!user?.email) return;
    setGmailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gmail/auth?userId=${encodeURIComponent(user.email)}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.authUrl) {
          window.location.href = result.authUrl;
        }
      }
    } catch (error) {
      console.error('Failed to initiate Gmail OAuth:', error);
    } finally {
      setGmailLoading(false);
    }
  };

  // Close account dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAccountDropdown) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-account-dropdown]')) {
          setShowAccountDropdown(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAccountDropdown]);

  const fetchCreditInfo = async () => {
    // Rate limiting: prevent calls more frequent than once per 10 seconds
    const now = Date.now();
    if (now - lastFetchTime < 10000) {
      console.log('Skipping credit fetch - rate limit protection');
      return;
    }
    setLastFetchTime(now);
    
    try {
      const response = await fetch(`${API_URL}/api/credits/usage`);
      if (response.ok) {
        const result = await response.json();
        // Handle both old and new API response structures
        if (result.data) {
          setCreditInfo({
            totalCredits: result.data.monthlyCredits || 0,
            usedCredits: result.data.usedCredits || 0,
            remainingCredits: result.data.remainingCredits || 0,
            nextInvoiceDate: result.data.nextInvoiceDate || '',
            usagePercentage: result.data.usagePercentage || result.data.percentage || 0,
            monthlyCredits: result.data.monthlyCredits || 0,
            billingPeriod: result.data.billingPeriod || '',
            remainingDays: result.data.remainingDays || 0,
            avgCreditsPerDay: result.data.avgCreditsPerDay || 0
          });
        } else {
          // Fallback to old structure
          setCreditInfo({
            totalCredits: result.monthlyCredits || 0,
            usedCredits: result.usedCredits || 0,
            remainingCredits: result.remainingCredits || 0,
            nextInvoiceDate: result.nextInvoiceDate || '',
            usagePercentage: result.usagePercentage || result.percentage || 0,
            monthlyCredits: result.monthlyCredits || 0,
            billingPeriod: result.billingPeriod || '',
            remainingDays: result.remainingDays || 0,
            avgCreditsPerDay: result.avgCreditsPerDay || 0
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch credit info:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getActiveTab = () => {
    const path = location.pathname;
    // 서브메뉴 경로로 부모 메뉴 찾기
    for (const tab of tabs) {
      if (tab.children) {
        for (const child of tab.children) {
          if (path.startsWith(child.path) || path.startsWith(`/${child.id}`)) {
            return tab.id;
          }
        }
      } else if (tab.path && path.startsWith(tab.path)) {
        return tab.id;
      }
    }
    // Handle legacy routes
    if (path === '/' || path === '/find' || path === '/collaborators') {
      return 'influencer-marketing';
    }
    return 'influencer-marketing'; // 기본값
  };

  // 현재 활성 탭의 children 가져오기
  const getActiveTabChildren = () => {
    const activeTabId = getActiveTab();
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    return activeTab?.children || null;
  };

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="tabs-container">
      <div className="tabs-header">
        <Link to="/" className="brand">
          <img 
            src="https://ssembleshortsbucket.blob.core.windows.net/web-assets/dstribute-logo.png" 
            alt="Dstribute Logo" 
            className="brand-logo"
          />
        </Link>
        
        <nav className="tabs-nav">
          {tabs.map((tab) => {
            // 외부 URL이 있으면 외부 링크로 처리
            if (tab.externalUrl) {
              return (
                <a
                  key={tab.id}
                  href={tab.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tab-button"
                >
                  <span className="tab-emoji">{tab.emoji}</span>
                  <span className="tab-label">{tab.label}</span>
                </a>
              );
            }
            // children이 있으면 첫 번째 child로 이동, 없으면 직접 path로 이동
            const linkPath = tab.children ? tab.children[0].path : (tab.path || `/${tab.id}`);
            return (
              <Link
                key={tab.id}
                to={linkPath}
                className={`tab-button ${getActiveTab() === tab.id ? 'active' : ''}`}
              >
                <span className="tab-emoji">{tab.emoji}</span>
                <span className="tab-label">{tab.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="theme-controls">
          {creditInfo && !loading && (
            <div className="credit-info" title={`Billing Period: ${creditInfo.billingPeriod || 'N/A'}\nUsage: ${creditInfo.usagePercentage}%\nDays Remaining: ${creditInfo.remainingDays || 0}\nAvg Credits/Day: ${formatNumber(creditInfo.avgCreditsPerDay || 0)}`}>
              <div className="credit-display">
                <span className="credit-value">{formatNumber(creditInfo.remainingCredits)}</span>
                <span className="credit-label">/ {formatNumber(creditInfo.totalCredits)}</span>
              </div>
              {creditInfo.billingPeriod && (
                <div className="credit-period">{creditInfo.billingPeriod}</div>
              )}
            </div>
          )}
          {user && (
            <div data-account-dropdown style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '12px', paddingLeft: '12px', borderLeft: '1px solid #e0e0e0', position: 'relative' }}>
              <button
                onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#666',
                  transition: 'all 0.2s ease',
                  maxWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#ccc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.email}>
                  {user.email}
                </span>
                <span style={{ fontSize: '10px', transform: showAccountDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
              </button>

              {showAccountDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '250px',
                    zIndex: 1000,
                    overflow: 'hidden'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #e0e0e0', fontSize: '12px', color: '#999', fontWeight: 500 }}>
                    Saved Accounts ({savedAccounts.length})
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {savedAccounts.map((account) => (
                      <div
                        key={account.user.email}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          cursor: account.user.email === user.email ? 'default' : 'pointer',
                          backgroundColor: account.user.email === user.email ? '#f0f7ff' : 'transparent',
                          borderLeft: account.user.email === user.email ? '3px solid #007bff' : '3px solid transparent',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (account.user.email !== user.email) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (account.user.email !== user.email) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                        onClick={() => {
                          if (account.user.email !== user.email) {
                            switchAccount(account.user.email);
                            setShowAccountDropdown(false);
                            window.location.reload();
                          }
                        }}
                      >
                        <span style={{
                          fontSize: '14px',
                          color: account.user.email === user.email ? '#007bff' : '#333',
                          fontWeight: account.user.email === user.email ? 500 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1
                        }}>
                          {account.user.email}
                        </span>
                        {account.user.email !== user.email && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAccount(account.user.email);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#999',
                              cursor: 'pointer',
                              padding: '4px',
                              fontSize: '12px',
                              lineHeight: 1,
                              borderRadius: '4px',
                              marginLeft: '8px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#ff4444';
                              e.currentTarget.style.backgroundColor = '#fff0f0';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#999';
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title="Remove account"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Gmail Integration Section */}
                  <div style={{ borderTop: '1px solid #e0e0e0' }}>
                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#999', fontWeight: 500 }}>
                      Gmail Integration
                    </div>
                    {gmailAccounts.length > 0 ? (
                      <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                        {gmailAccounts.map((account) => (
                          <div
                            key={account.email}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px 12px',
                              fontSize: '13px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                              <span style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: account.status === 'active' ? '#22c55e' : '#ef4444',
                                flexShrink: 0
                              }} />
                              <span style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#333'
                              }} title={account.email}>
                                {account.email}
                              </span>
                            </div>
                            <span style={{
                              fontSize: '11px',
                              color: account.status === 'active' ? '#22c55e' : '#ef4444',
                              marginLeft: '8px',
                              flexShrink: 0
                            }}>
                              {account.status === 'active' ? 'Connected' : 'Expired'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div style={{ padding: '8px 12px', fontSize: '13px', color: '#999' }}>
                          No Gmail accounts connected
                        </div>
                        <button
                          onClick={handleGmailConnect}
                          disabled={gmailLoading}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            background: 'transparent',
                            border: 'none',
                            borderTop: '1px solid #f0f0f0',
                            cursor: gmailLoading ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            color: '#ea4335',
                            textAlign: 'left',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: gmailLoading ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!gmailLoading) e.currentTarget.style.backgroundColor = '#fef2f2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                          </svg>
                          {gmailLoading ? 'Connecting...' : '+ Connect Gmail'}
                        </button>
                      </>
                    )}
                  </div>

                  <div style={{ borderTop: '1px solid #e0e0e0' }}>
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowAccountDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#666',
                        textAlign: 'left',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      Sign Out
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        setShowAccountDropdown(false);
                        navigate('/login');
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: 'transparent',
                        border: 'none',
                        borderTop: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#007bff',
                        textAlign: 'left',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      + Add Another Account
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 서브 메뉴 바 */}
      {getActiveTabChildren() && (
        <div className="submenu-bar">
          <div className="submenu-container">
            {getActiveTabChildren()!.map((child) => (
              <Link
                key={child.id}
                to={child.path}
                className={`submenu-item ${location.pathname.startsWith(child.path) || location.pathname.startsWith(`/${child.id}`) ? 'active' : ''}`}
              >
                <span className="submenu-emoji">{child.emoji}</span>
                <span className="submenu-label">{child.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tabs;